import React, { useEffect, useState, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Avatar,
  Box,
  Button,
  Chip,
  debounce,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from "@mui/x-data-grid";
import {
  AddCircleOutline,
  Checklist,
  EditSquare,
  History,
  SearchOutlined,
} from "@mui/icons-material";

import { RootState } from "../../store/Index";
import { DataGrid } from "../../components/common/Index";
import { RoleItem } from "./components/SearchRoleDialog";
import { SearchRoleDialog } from "./components/SearchRoleDialog";

import { TrashIcon } from "../../components/icons";

import { ChecklistUsageProps } from "../../data/checklists";
import { tokens } from "../../theme";
import { getStatusColors } from "../../constants/statusColors";
import apiService from "../../service/Api";

type TaskItem = {
  id?: number;
  name: string;
  points?: number;
  status: "pending" | "completed";
};

type ChecklistTask = {
  id?: number;
  title: string;
  task_item: TaskItem[];
};

export type LocalChecklistProps = {
  id: number;
  title: string;
  role: string;
  type: string;
  application: string;
  active: boolean;
  usage: number;
  compliance: number;
  lastUpdate: {
    date: string;
    updatedBy: string;
  };
  tasks: ChecklistTask[];
};
interface ChecklistListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ChecklistApiResponse[];
}

interface ChecklistApiResponse {
  id: number;
  name: string;
  roles_name?: string;
  checklist_type?: string;
  phase?: string;
  sections?: {
    id?: number;
    name: string;
    list_items?: {
      id?: number;
      name: string;
      points?: number;
      status?: "pending" | "completed";
    }[];
  }[];
  updated_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const Checklists: React.FC = () => {
  const [searchRoleOpen, setSearchRoleOpen] = useState(false);
  const [, setAddTaskOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<"all" | "active" | "inactive">(
    "all"
  );

  const [checklistData, setChecklistData] = useState<LocalChecklistProps[]>([]);

  const [usageData] = useState<ChecklistUsageProps[]>([]);

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  console.log({ searchTerm, filterType });

  const navigate = useNavigate();

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const statusColors = getStatusColors(theme.palette.mode);

  const primaryColor = colors.primary[400];
  const primaryTextColor = colors.primary[400];
  const secondaryTextColor = colors.neutral[100];
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const checklist = async () => {
    try {
      const response = await apiService.get<ChecklistListResponse>(
        "/api/checklists/checklist/"
      );

      console.log({ response });

      const PHASE_MAP: Record<string, string> = {
        "Pre-stream": "pre-shoot",
        "On-stream": "on-shoot",
        "Post-stream": "post-shoot",
      };

      const formatted: LocalChecklistProps[] = response.data.results.map(
        (item) => ({
          id: item.id,
          title: item.name,
          role: item.roles_name || "Unknown",
          type: item.checklist_type || "Unknown",
          application: PHASE_MAP[item.phase || "Pre-stream"] || "pre-shoot",
          active: true,
          usage: item.sections?.length ?? 0,
          compliance: 0,
          lastUpdate: {
            date: item.updated_at,
            updatedBy: "System",
          },
          tasks:
            item.sections?.map((section) => ({
              id: section.id, // 👈 KEEP ID
              title: section.name,
              task_item:
                section.list_items?.map((i) => ({
                  id: i.id, // 👈 KEEP ID
                  name: i.name,
                  status: i.status || "pending",
                  points: i.points ?? 0,
                })) || [],
            })) || [],
        })
      );
      console.log("Formatted checklists:", formatted);

      setChecklistData(formatted);
    } catch (error) {
      console.error("Checklist fetch error:", error);
    }
  };
  useEffect(() => {
    checklist();
  }, []);

  const handleDeleteChecklist = async (checklistId: number) => {
    try {
      console.log("Deleting checklist:", checklistId);
      await apiService.delete(`/api/checklists/checklist/${checklistId}/`);

      setChecklistData((prev) =>
        prev.filter((item) => item.id !== checklistId)
      );

    } catch (error) {
      console.error("Delete checklist failed:", error);
    }
  };

  const handleAddChecklist = async (data: {
    role: RoleItem | null;
    checklistItem: string;
    usage: string | null;
    description: string;
    tasks: {
      title: string;
      task_item: {
        name: string;
        points?: number;
        status: "pending" | "completed";
      }[];
    }[];
  }) => {
    if (!data.checklistItem || !data.role || !data.usage) {
      console.warn("Missing required fields");
      return;
    }

    const PHASE_MAP: Record<string, string> = {
      "Pre-stream": "pre_stream",
      "On-stream": "on_stream",
      "Post-stream": "post_stream",
    };

    const payload = {
      name: data.checklistItem,
      description: data.description || null,
      roles: data.role.id,
      phase: PHASE_MAP[data.usage],
      notes: null,
      sections: data.tasks.map((task, sectionIndex) => ({
        name: task.title,
        description: null,
        order: sectionIndex,
        list_items: task.task_item.map((item, itemIndex) => ({
          name: item.name,
          description: null,
          points: item.points,
          order: itemIndex,
        })),
      })),
    };

    try {
      await apiService.post(
        "/api/checklists/checklist/full-create/",
        payload
      );

      // ---- Optimistically add checklist to UI ----
      const newChecklist: LocalChecklistProps = {
        id: Date.now(),
        title: data.checklistItem,
        role: data.role.name,
        type: "Unknown", // or map if you have a type
        application: data.usage.toLowerCase().replace("-", "_"), // match your PHASE_MAP
        active: true,
        usage: data.tasks.length,
        compliance: 0,
        lastUpdate: {
          date: new Date().toISOString(),
          updatedBy: "You",
        },
        tasks: data.tasks,
      };

      setChecklistData((prev) => [newChecklist, ...prev]);

      console.log("Checklist added successfully:", newChecklist);
    } catch (error) {
      console.error("Checklist full-create failed:", error);
    }
  };

  const filteredChecklists = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return checklistData.filter((item) => {
      const matchesSearch =
        item.title.toLowerCase().includes(term) ||
        item.role.toLowerCase().includes(term) ||
        item.application.toLowerCase().includes(term);

      const matchesStatus =
        filterType === "all" ||
        (filterType === "active" && item.active) ||
        (filterType === "inactive" && !item.active);

      return matchesSearch && matchesStatus;
    });
  }, [checklistData, searchTerm, filterType]);

  const filteredUsage = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return usageData.filter((item) => {
      return (
        item.event.name.toLowerCase().includes(term) ||
        item.checklist_title.toLowerCase().includes(term) ||
        item.pilot.name.toLowerCase().includes(term)
      );
    });
  }, [usageData, searchTerm]);

  const [editChecklist, setEditChecklist] =
    useState<LocalChecklistProps | null>(null);

  const getApplicationChipProps = (application: string) => {
    switch (application.toLowerCase()) {
      case "pre-shoot":
        return {
          label: "Pre Shoot",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case "post-shoot":
        return {
          label: "Post Shoot",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "mid-shoot":
        return {
          label: "Mid Shoot",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      case "post-production":
        return {
          label: "Post Production",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "final-cut":
        return {
          label: "Final Cut",
          bgcolor: "#80008040",
          color: "#800080",
        };
      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  const getStatusChipProps = (status: string) => {
    switch (status.toLowerCase()) {
      case "complete":
        return {
          label: "Complete",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };
      case "partial":
        return {
          label: "Partial",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };
      case "pending":
        return {
          label: "Pending",
          bgcolor: statusColors.fulfilled.bg,
          color: statusColors.fulfilled.fg,
        };
      default:
        return {
          label: "Unknown",
          bgcolor: statusColors.pending.bg,
          color: statusColors.pending.fg,
        };
    }
  };

  const getComplianceProps = (compliance: number) => {
    if (compliance >= 80) {
      return "#4BA663";
    } else if (compliance < 80 && compliance >= 65) {
      return "#0000FF";
    } else if (compliance < 65 && compliance >= 50) {
      return "#F79E1B";
    } else if (compliance < 50) {
      return "#EB4335";
    }
  };

  useEffect(() => {
  }, [checklistData]);

  const checklistsColumns: GridColDef<LocalChecklistProps>[] = [
    {
      field: "title",
      headerName: "Checklist",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    {
      field: "role",
      headerName: "Role & Usage",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 0.4 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.role}
          </Typography>
          <Chip
            label={getApplicationChipProps(params.row.application).label}
            sx={{
              color: getApplicationChipProps(params.row.application).color,
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: getApplicationChipProps(params.row.application).bgcolor,
            }}
            size={"small"}
          />
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ lineHeight: 1, py: 1 }}
          gap={1}
        >
          <Switch
            checked={params.row.active}
            color={params.row.active ? "primary" : "default"}
            slotProps={{ input: { "aria-label": "controlled" } }}
          />
          <Typography variant={"caption"}>
            {params.row.active ? "Active" : "Inactive"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "lastUpdate",
      headerName: "Last Update",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography variant={"subtitle2"}>
            {format(new Date(params.row.lastUpdate.date), "MMM dd, yyyy")}
          </Typography>
          <Typography variant={"caption"}>
            {params.row.lastUpdate.updatedBy}
          </Typography>
        </Box>
      ),
    },
    {
      field: "compliance",
      headerName: "Usage & Compliance",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography
            variant={"subtitle2"}
          >{`${params.row.usage} Events`}</Typography>
          <Typography
            variant={"caption"}
            sx={{ color: getComplianceProps(params.row.compliance) }}
          >
            {`${params.row.compliance}% compliance`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ py: 1, lineHeight: 1 }}
          gap={1}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setEditChecklist(params.row); // 👈 pass full checklist
              setSearchRoleOpen(true);
            }}
          >
            <EditSquare color="primary" sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation(); // ⬅️ Prevent row click
              handleDeleteChecklist(params.row.id);
            }}
          >
            <TrashIcon color="error" sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const usageColumns: GridColDef<ChecklistUsageProps>[] = [
    {
      field: "event",
      headerName: "Event/Stream",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.row.event.name}
          </Typography>
          <Typography variant="caption" color={"#757575"} fontWeight="medium">
            {params.row.event.type}
          </Typography>
        </Box>
      ),
    },
    {
      field: "checklist",
      headerName: "Checklist",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => (
        <Box sx={{ lineHeight: 1, py: 0.4 }}>
          <Typography variant="body2" fontWeight="medium">
            {params.row.checklist_title}
          </Typography>
          <Chip
            label={getApplicationChipProps(params.row.application).label}
            sx={{
              color: getApplicationChipProps(params.row.application).color,
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: getApplicationChipProps(params.row.application).bgcolor,
            }}
            size={"small"}
          />
        </Box>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography variant={"subtitle2"}>
            {format(new Date(params.row.date), "MMM dd, yyyy")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "pilot",
      headerName: "Crew Member",
      flex: 1,
      renderCell: (params) => (
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ lineHeight: 1, py: 1 }}
          gap={1}
        >
          <Avatar
            src={""}
            sx={{
              width: 32,
              height: 32,
              bgcolor: colors.primary[800],
              color: colors.primary[100],
            }}
          />
          <Box>
            <Typography variant={"subtitle2"}>
              {params.row.pilot.name}
            </Typography>
            <Typography color={"#757575"} variant={"caption"}>
              {params.row.pilot.role}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "completion",
      headerName: "Completion",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 1, lineHeight: 1 }}>
          <Typography
            variant={"body2"}
            sx={{ color: getComplianceProps(params.row.completion) }}
          >{`${params.row.completion}%`}</Typography>
          <Typography variant={"caption"}>
            {`${params.row.tasks_completed}/${params.row.tasks} tasks`}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ py: 1.6, lineHeight: 1 }}
        >
          <Chip
            label={getStatusChipProps(params.row.status).label}
            sx={{
              color: getStatusChipProps(params.row.status).color,
              borderRadius: 1,
              fontWeight: 600,
              bgcolor: getStatusChipProps(params.row.status).bgcolor,
            }}
            size={"small"}
          />
        </Box>
      ),
    },
  ];

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.clear();
    };
  }, [debouncedSetSearchTerm]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const handleRowClick = (params: GridRowParams<LocalChecklistProps>) => {
    // navigate(`/checklists/${params.row.id}`, {
    //   state: { checklist: params.row },
    navigate(`/crew-management/checklists/${params.row.id}`, {
      state: { checklist: params.row },
    });
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Checklist sx={{ fontSize: 20 }} />
                <Typography variant={"body2"}>All Checklists</Typography>
              </Box>
            }
          />
          <Tab
            label={
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <History sx={{ fontSize: 20 }} />
                <Typography variant={"body2"}>Usage History</Typography>
              </Box>
            }
          />
        </Tabs>
      </Box>

      <AnimatePresence mode="wait">
        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              height: "100%",
              background: colors.primary[900],
              padding: "24px",
              borderRadius: 5,
              boxShadow: 0,
            }}
          >
            <Box
              display={"flex"}
              mb={"16px"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={"4px"}
            >
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Box
                  sx={{
                    backgroundColor: backgroundColor,
                    border: "1px solid",
                    borderRadius: 2,
                    borderColor: "primary",
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    flex: 1,
                    minWidth: 320,
                  }}
                >
                  <SearchOutlined
                    sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }}
                  />
                  <InputBase
                    placeholder="Search by name, role or usage..."
                    sx={{ padding: 0.4, flex: 1, color: primaryTextColor }}
                    onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                  />
                </Box>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel
                    id="checklist-status-label"
                    sx={{ color: primaryTextColor }}
                  >
                    Checklist Status
                  </InputLabel>
                  <Select
                    labelId="checklist-status-label"
                    id="checklist-status-label"
                    value={filterType}
                    label="Checklist Status"
                    onChange={(e) =>
                      setFilterType(
                        e.target.value as "all" | "active" | "inactive"
                      )
                    }
                    sx={{
                      background: backgroundColor,
                      color: primaryTextColor,
                      borderRadius: 2,
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: primaryColor,
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                size="small"
                startIcon={<AddCircleOutline />}
                sx={{ textTransform: "capitalize" }}
                variant="contained"
                onClick={() => setSearchRoleOpen(true)}
              >
                Add Checklist
              </Button>
              {/* <Button
                size="small"
                startIcon={<AddCircleOutline />}
                sx={{ textTransform: "capitalize" }}
                variant="contained"
                onClick={() => checklistItem()}
              >
                Testing Add Checklist Item
              </Button> */}
              <SearchRoleDialog
                searchRoleOpen={searchRoleOpen}
                setSearchRoleOpen={setSearchRoleOpen}
                searchResults={selectedRole}
                setSearchResults={setSelectedRole}
                setAddRoleOpen={setAddTaskOpen}
                setAddTaskOpen={setAddTaskOpen}
                onAddChecklist={handleAddChecklist}
                checklist={() => checklist()}
                editChecklist={editChecklist} // 👈 NEW
                mode={editChecklist ? "edit" : "add"} // 👈 NEW
              />
            </Box>

            <Box>
              <DataGrid
                rows={filteredChecklists}
                columns={checklistsColumns}
                rowCount={filteredChecklists.length}
                paginationMode="server"
                paginationModel={paginationModel}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              height: "100%",
              background: colors.primary[900],
              padding: "24px",
              borderRadius: 5,
              boxShadow: 0,
            }}
          >
            <Box
              display={"flex"}
              mb={"16px"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={"4px"}
            >
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Box
                  sx={{
                    backgroundColor: backgroundColor,
                    border: "1px solid",
                    borderRadius: 2,
                    borderColor: "primary",
                    display: "flex",
                    alignItems: "center",
                    pl: 1,
                    flex: 1,
                    minWidth: 320,
                  }}
                >
                  <SearchOutlined
                    sx={{ fontSize: 24, mr: 1, color: secondaryTextColor }}
                  />
                  <InputBase
                    placeholder="Search by name, role or usage..."
                    sx={{ padding: 0.4, flex: 1, color: primaryTextColor }}
                    onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                  />
                </Box>
              </Box>
              <Button
                size={"small"}
                startIcon={<AddCircleOutline />}
                sx={{ textTransform: "capitalize" }}
                variant="contained"
              >
                Add Checklist
              </Button>
            </Box>

            <Box>
              <DataGrid
                rows={filteredUsage}
                columns={usageColumns}
                rowCount={filteredUsage.length}
                paginationMode="server"
                paginationModel={paginationModel}
                onRowClick={handleRowClick}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={[5, 10, 15, 20]}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor:
                      colors.primary[500],
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor:
                      colors.primary[500],
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    color: colors.primary[100],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: colors.primary[900],
                    justifyContent: "center",
                    padding: 1,
                  },
                }}
              />
            </Box>
          </Box>
        </TabPanel>
      </AnimatePresence>
      <></>
    </Box>
  );
};

export default Checklists;
